import React, { useEffect, useState } from 'react';
import Modal from '../../../../shared/components/core-ui/modal/modal.component';
import Button, { ButtonType } from '../../../../shared/components/core-ui/button/button.component';
import { InputText } from '../../../../shared/components/core-ui/input-text/input-text.component';
import { TeamUiModel } from '../../../../shared/ui-models/team.ui-model';
import { AccessControlFilterConfigModel } from '../../../../shared/models/team.model';

export interface TeamFormValue {
  name: string;
  accessControl: { module: string; enforcedFilters: { key: string; value: string[] }[] }[];
}

interface TeamFormModalProps {
  show: boolean;
  team?: TeamUiModel;
  /** The allow-list the server validates against. Pickers are built from it. */
  filterConfig: AccessControlFilterConfigModel;
  onClose: () => void;
  onSubmit: (value: TeamFormValue) => void;
}

export const TeamFormModal: React.FC<TeamFormModalProps> = ({
  show,
  team,
  filterConfig,
  onClose,
  onSubmit
}) => {
  const isEdit = Boolean(team);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<Record<string, string[]>>({});

  const leadFilters = filterConfig.leads ?? {};

  useEffect(() => {
    setName(team?.name ?? '');
    const grant = team?.grants.find((item) => item.module === 'leads');
    const next: Record<string, string[]> = {};
    (grant?.filters ?? []).forEach((filter) => {
      next[filter.key] = filter.values;
    });
    setSelected(next);
  }, [team, show]);

  const toggleValue = (key: string, value: string) => {
    const current = selected[key] ?? [];
    const next = current.indexOf(value) >= 0 ? current.filter((v) => v !== value) : current.concat(value);
    const updated = { ...selected };
    if (next.length) updated[key] = next;
    else delete updated[key];
    setSelected(updated);
  };

  const filterKeys = Object.keys(leadFilters);
  const hasAnyFilter = Object.keys(selected).length > 0;

  return (
    <Modal
      config={{ showModal: show, fullScreen: false, maxWidth: 'md', showClose: true, autoHeight: true }}
      eventHandlers={{ closeModalHandler: onClose }}
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        <h2 className="text-lg font-semibold">{isEdit ? 'Edit team' : 'Create team'}</h2>

        <div>
          <label className="mb-1 block text-sm text-gray-600">Name</label>
          <InputText
            data={{ value: name, placeholder: 'e.g. Acquisition — Cairo' }}
            config={{ required: true }}
            eventHandlers={{ onChangeHandler: (value: string) => setName(value) }}
          />
        </div>

        <div>
          <h3 className="text-sm font-medium">Lead access</h3>
          <p className="mb-2 text-xs text-gray-500">
            Pick nothing and the team gets an unconditional org-wide browse of leads. Pick values
            and the browse is narrowed to them. This grant widens what managers can see beyond
            their own downline — it never narrows an agent&apos;s own book.
          </p>

          {filterKeys.length === 0 ? (
            <p className="text-xs text-gray-500">No filter keys configured.</p>
          ) : (
            filterKeys.map((key) => (
              <div key={key} className="mb-3">
                <div className="mb-1 text-sm text-gray-700">
                  {key}
                  <span className="ml-2 text-xs text-gray-400">{leadFilters[key].description}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(leadFilters[key].allowedValues ?? []).map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => toggleValue(key, value)}
                      className={`rounded-full border px-3 py-1 text-sm ${
                        (selected[key] ?? []).indexOf(value) >= 0
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 text-gray-600'
                      }`}
                    >
                      {value}
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}

          {!hasAnyFilter ? (
            <p className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
              This team will be able to browse <strong>every</strong> lead.
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-3 px-6 pb-6 sm:flex-row sm:justify-end">
        <Button type={ButtonType.gray} onClick={onClose}>
          Cancel
        </Button>
        <Button
          type={ButtonType.primary}
          disabled={name.trim().length === 0}
          tracking={{
            event: 'record_saved',
            entity: 'team',
            action: isEdit ? 'update' : 'create',
            outcome: 'success'
          }}
          onClick={() =>
            onSubmit({
              name: name.trim(),
              accessControl: [
                {
                  module: 'leads',
                  enforcedFilters: Object.keys(selected).map((key) => ({
                    key,
                    value: selected[key]
                  }))
                }
              ]
            })
          }
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </div>
    </Modal>
  );
};
